import React from 'react';
import { ArrowRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const ClinicianLeadersSection = () => {
  const articles = [
    {
      category: 'Health System Executive',
      title: 'Driving Operational Efficiency in a Health System with 54 Clinics & 300+ Providers',
      description: "A health system CMO & SVP shares his perspectives on how Asha's AI is transforming care in outpatient practices.",
      author: {
        name: 'Dr. Mark Smith, MD',
        role: 'CMO, Regional Dental Network',
        image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400&auto=format&fit=crop',
      },
      date: 'Sep 23th, 2024',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop',
      link: '/blog/operational-efficiency-health-system',
    },
    {
      category: 'Health System Executive',
      title: 'Observations from Leading Health Systems & Growing Care Organizations',
      description: "Asha's AI goes beyond operational efficiency and improve the financial health of outpatient practices. It improves patient care.",
      author: {
        name: 'Dr. Steven Rivera, DO, MBA',
        role: 'VP Operations, Dental Care Partners',
        image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400&auto=format&fit=crop',
      },
      date: 'Sep 23th, 2024',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop',
      link: '/blog/improving-patient-care',
    },
  ];

  const investorLogos = [
    { name: 'Y Combinator', src: '/logos/ycombinator.png', height: 'h-8' },
    { name: 'General Catalyst', src: '/logos/generalcatalyst.png', height: 'h-8' },
    { name: 'Rebel Fund', src: '/logos/rebelfund 1.png', height: 'h-7' },
    { name: 'Heuristic Capital', src: '/logos/heuristiccapital.png', height: 'h-9' },
    { name: 'Berkeley', src: '/logos/berkeley.png', height: 'h-10' },
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            Hear from{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Senior Clinician Leaders
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real insights from dental executives and practice leaders on how Asha AI is transforming their operations
          </p>
        </div>

        {/* Carousel */}
        <div className="mx-auto px-4 md:px-8">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4 pb-4">
              {articles.map((article, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-[30%] h-full">
                  <div className="h-full py-2">
                    <a
                      href={article.link}
                      className="block h-full group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                    >
                      {/* Image */}
                      <div className="relative h-48 sm:h-52 shrink-0 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                        
                        {/* Category Badge */}
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-white/95 backdrop-blur-md text-blue-900 text-[11px] font-bold tracking-wide uppercase rounded-full shadow-sm">
                            {article.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow">
                        <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2 leading-snug">
                          {article.title}
                        </h3>
                        
                        <p className="text-sm text-gray-600 mb-6 leading-relaxed line-clamp-3 flex-grow">
                          {article.description}
                        </p>

                        {/* Author Info */}
                        <div className="flex items-center justify-between pt-5 border-t border-gray-100 mt-auto">
                          <div className="flex items-center space-x-3">
                            <img
                              src={article.author.image}
                              alt={article.author.name}
                              className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm"
                            />
                            <div>
                              <div className="text-sm font-semibold text-gray-900 line-clamp-1">
                                {article.author.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {article.date}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1 text-blue-600 font-medium text-xs group-hover:translate-x-1 transition-transform shrink-0">
                            <span>Read</span>
                            <ArrowRight className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                    </a>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="-left-4 lg:-left-12" />
              <CarouselNext className="-right-4 lg:-right-12" />
            </div>
          </Carousel>
        </div>

        {/* View All Link */}
        <div className="text-center mt-12 mb-20">
          <a
            href="/blog"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-semibold text-lg transition-colors"
          >
            <span>View all articles</span>
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        {/* Built By Section - Futuristic Style */}
        <div className="relative rounded-3xl overflow-hidden bg-[#0B1120] text-white shadow-2xl">
          {/* Futuristic Background Elements */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-20"></div>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-gradient-to-b from-blue-500/10 via-transparent to-transparent pointer-events-none"></div>
          
          <div className="relative z-10 px-8 py-16 md:py-20 text-center">
            <h3 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
              Built and backed by the best.
            </h3>
            
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-16 leading-relaxed font-light">
              Asha is the only dental AI company backed by tier one institutional investment firms and built by AI experts from top research institutions.
            </p>

            <div className="flex flex-col items-center">
              
              <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8">
                {investorLogos.map((logo, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-center group relative"
                  >
                    <img
                      src={logo.src}
                      alt={logo.name}
                      className={`${logo.height} w-auto object-contain brightness-0 invert opacity-50 group-hover:opacity-100 transition-all duration-500 ease-out`}
                    />
                    {/* Hover Glow Effect */}
                    <div className="absolute inset-0 bg-white/20 blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 rounded-full"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ClinicianLeadersSection;